import {
  DailyInventoryChangesDoc,
  InventoryBinLocationDoc,
  OutboundReferenceCollection,
  ProductInventoryDoc,
  ProductOutboundOperation,
  ProductOutboundTransactionDoc,
  ProductSupplierDoc,
} from '@gooduncles/gu-app-schema';
import { groupBy, partition, sumBy } from 'lodash-es';

export type ProcessedOutboundTransaction = {
  inventoryId: string;
  inventoryQuantity: number;
  purchaseQuantity: number;
  totalQuantity: number;
  inventory: ProductInventoryDoc | undefined;
  productSupplier: ProductSupplierDoc | undefined;
  inventoryBinLocation: string | null;
  isPreOrder: boolean;
  ea?: { cost: number };
  bundle?: { cost: number };
};

/**
 * 재고 위치를 비교합니다.
 * 'A-01-F1' > 'A-02-F1' > 'B-04-P1'
 */
export const compareBinLocation = (aLocation: string | null, bLocation: string | null) => {
  if (aLocation === null && bLocation === null) {
    return 0; // 순서를 변경하지 않음
  }
  if (aLocation === null) {
    return 1; // a를 배열의 뒤로 보냄
  }
  if (bLocation === null) {
    return -1; // b를 배열의 뒤로 보냄
  }
  return aLocation.localeCompare(bLocation, 'en', { numeric: true });
};

/**
 * inventoryId별로 operation 타입에 따른 quantity 합계를 계산하는 함수
 * @param preOrderInventoryIds 선주문 상품 inventoryId Set. 해당 상품은 totalQuantity에서 purchaseQuantity 제외
 */
export const processOutboundTransactions = (
  txns: ProductOutboundTransactionDoc[],
  inventories: ProductInventoryDoc[],
  productSuppliers: ProductSupplierDoc[],
  inventoryBinLocations: InventoryBinLocationDoc['inventoryBinLocation'] | undefined,
  changes: DailyInventoryChangesDoc['changes'] | undefined,
  preOrderInventoryIds?: Set<string>
): ProcessedOutboundTransaction[] => {
  const inventoryMap = new Map(inventories.map((inv) => [inv._id, inv]));
  const supplierMap = new Map(productSuppliers.map((ps) => [ps._id, ps]));
  const txnsGroupByInventoryId = groupBy(txns, 'inventoryId');

  return Object.entries(txnsGroupByInventoryId)
    .map(([inventoryId, txns]) => {
      const inventory = inventoryMap.get(inventoryId);
      const productSupplier = supplierMap.get(inventory?.purchaseConfig.supplierId ?? '');
      const [inventoryTxns, purchaseTxns] = partition(
        txns,
        (txn) => txn.operation === ProductOutboundOperation.INVENTORY
      );
      const [orderPurchaseTxns, nonOrderPurchaseTxns] = partition(
        purchaseTxns,
        (txn) => txn.referenceCollection === OutboundReferenceCollection.ORDER
      );
      const inventoryQuantity = sumBy(inventoryTxns, 'requestQuantity');
      const orderPurchaseQty = sumBy(orderPurchaseTxns, 'requestQuantity');
      const nonOrderPurchaseQty = sumBy(nonOrderPurchaseTxns, 'requestQuantity');
      const isPreOrderInventory = preOrderInventoryIds?.has(inventoryId) ?? false;
      // 선주문 상품: ORDER → 발주량, 그 외 → 필요량 / 일반 상품: 전체 발주량이 필요량에도 포함
      const purchaseQuantity = isPreOrderInventory ? orderPurchaseQty : orderPurchaseQty + nonOrderPurchaseQty;
      const totalQuantity = isPreOrderInventory
        ? inventoryQuantity + nonOrderPurchaseQty
        : inventoryQuantity + orderPurchaseQty + nonOrderPurchaseQty;
      const inventoryBinLocation = inventoryBinLocations?.[inventoryId] ?? null;

      return {
        inventoryId,
        inventoryQuantity,
        purchaseQuantity,
        totalQuantity,
        inventory,
        productSupplier,
        inventoryBinLocation,
        isPreOrder: isPreOrderInventory,
        ea: changes?.[inventoryId]?.ea,
        bundle: changes?.[inventoryId]?.bundle,
      };
    })
    .sort((a, b) => {
      // 1순위 - 구분
      const groupCompare = (a.productSupplier?.groupId ?? '').localeCompare(b.productSupplier?.groupId ?? '');
      if (groupCompare !== 0) return groupCompare;
      // 2순위 - 창고 위치
      const binCompare = compareBinLocation(a.inventoryBinLocation, b.inventoryBinLocation);
      if (binCompare !== 0) return binCompare;
      // 3순위 - 상품명
      const nameCompare = (a.inventory?.inventoryName ?? '').localeCompare(b.inventory?.inventoryName ?? '');
      if (nameCompare !== 0) return nameCompare;
      // 4순위 - 거래처명
      return (a.productSupplier?.name ?? '').localeCompare(b.productSupplier?.name ?? '');
    });
};
